<script setup lang="ts">
import { useWorkspaces } from '@/modules/Workspaces/useWorkspaces'
import { doExportProject } from '@/shared/utils/exports'
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import CircularProgress from '@/uiKit/CircularProgress.vue'
import IconButton from '@/uiKit/IconButton.vue'
import { computed, ref } from 'vue'
import { usePermissionsStore } from '../IdentityAndAccess/permissionsStore'
import ExportButton from './ExportButton.vue'
import { useProject } from './useProject'

const props = defineProps<{
  workspaceId: string
  projectId: string
}>()

const { captureAnalyticsEvent } = useAnalytics()

const workspacesStore = useWorkspaces()
const projectStore = useProject()
const permissionsStore = usePermissionsStore()
const exporting = ref({ csv: false, xlsx: false })
const showExportProgress = ref(false)
const isAnyExportInProgress = computed(() => exporting.value.csv || exporting.value.xlsx)

const onExport = async (format: 'csv' | 'xlsx') => {
  captureAnalyticsEvent(ANALYTICS_EVENT.PROJECT_EXPORTED)
  exporting.value[format] = true
  showExportProgress.value = true
  try {
    await doExportProject(props.workspaceId, props.projectId, 200, 3000, format) // keep trying for max 200 times (sleep 3000ms = 3s) = 10m
    captureAnalyticsEvent(ANALYTICS_EVENT.PROJECT_EXPORTED)
  } finally {
    exporting.value[format] = false
    showExportProgress.value = false
  }
}
</script>

<template>
  <ExportButton
    v-if="
      workspacesStore.currentWorkspace?.id &&
      projectStore.projectId &&
      permissionsStore.currentProjectPermissions.manage_project_exports
    "
    :loading="isAnyExportInProgress"
    v-bind="$attrs"
    @click="onExport"
  />
  <div
    class="absolute bottom-3 z-9 transition-all duration-300 ease-in"
    :class="{
      'right-3 opacity-100': showExportProgress,
      'right-[-420px] opacity-0': !showExportProgress,
    }"
    aria-labelledby="export-in-progress-dialog-label"
    role="dialog"
  >
    <div
      class="flex h-16 w-[420px] grow items-center justify-between gap-1 rounded-corner-8 border border-icon-gray-subtle bg-surface-primary p-2 shadow-md"
    >
      <div class="flex items-center justify-center gap-2">
        <div class="flex size-7 items-center justify-center">
          <CircularProgress
            size="xs"
            class="animate-spin rounded-full"
          />
        </div>
        <div class="flex flex-col items-center justify-start gap-2">
          <p class="text-sm-12px-default text-text">Export in progress</p>
        </div>
      </div>
      <IconButton
        size="sm"
        icon="close"
        rounded
        variant="transparent"
        aria-label="Close export menu"
        name="Close export menu"
        role="button"
        @click="showExportProgress = false"
      >
        Export
      </IconButton>
    </div>
  </div>
</template>
